"use client";

import { BaseNavbar } from "@/components/base-navbar";
import { NavLink } from "@/components/nav-link";
import { Zillow } from "@/components/svgs/zillow";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserAvatar } from "@/components/ui/user-avatar";
import { usePropertyChat } from "@/contexts/property-chat-context";
import { useScroll } from "@/hooks/use-scroll";
import { useUser } from "@/hooks/useUser";
import { shareContent } from "@/lib/share";
import { cn } from "@/lib/utils";
import { LogOut, Menu, MessageSquare, Share2, X } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

interface PropertyNavbarProps {
  propertyId?: string;
  propertyUrl?: string;
}

export function PropertyNavbar({
  propertyId,
  propertyUrl,
}: PropertyNavbarProps) {
  const { user, avatarUrl, handleSignIn, handleSignOut } = useUser();
  const { openChat, setPropertyId } = usePropertyChat();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { y } = useScroll();

  const handleShare = async () => {
    const url = window.location.href;
    await shareContent({ url });
  };

  const handleChat = () => {
    if (propertyId) {
      setPropertyId(propertyId);
      openChat();
    }
  };

  const mobileMenuContent = (
    <div className="mt-4">
      {user ? (
        <div className="flex flex-col gap-3">
          <Link href="/app">
            <button
              className="w-full rounded-full bg-canary px-4 py-2 text-left font-bold text-smokeyBlack transition-colors hover:bg-canary/90"
              type="button"
            >
              Go to Dashboard
            </button>
          </Link>
          <button
            onClick={handleSignOut}
            className="rounded-full text-left font-bold"
            type="button"
          >
            <LogOut size={16} className="mr-2 inline" />
            Logout
          </button>
        </div>
      ) : (
        <div className="flex flex-col">
          <NavLink href="/login" className="font-bold">
            Sign In
          </NavLink>
        </div>
      )}
    </div>
  );

  return (
    <BaseNavbar
      className={cn(
        "bg-white border-b px-4 md:px-8 sticky top-0 left-0 right-0 z-50 transition-shadow duration-200",
        y > 0 && "shadow-sm",
      )}
      mobileMenuContent={isMenuOpen && mobileMenuContent}
      extended
    >
      <div className="flex items-center justify-between w-full">
        <div className="flex-1" />
        <div className="flex items-center gap-6">
          <div className="md:hidden flex items-center gap-2">
            {propertyUrl && (
              <Link
                href={propertyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
              >
                <Zillow />
              </Link>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={handleShare}
            >
              <Share2 className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={handleChat}
            >
              <MessageSquare className="h-5 w-5" />
            </Button>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <Link href="/app">
                  <Button
                    variant="default"
                    className="rounded-full bg-canary font-bold text-smokeyBlack hover:bg-canary/90"
                  >
                    Go to Dashboard
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="relative h-8 w-8 rounded-full"
                    >
                      <UserAvatar avatarUrl={avatarUrl} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button onClick={handleSignIn} className="rounded-full font-bold">
                Sign In
              </Button>
            )}
          </div>

          <button
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            type="button"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
    </BaseNavbar>
  );
}
